import {useEffect, useMemo, useRef, useState} from "react";

/**
 * Aperçu de la page depuis la fiche de campagne du back-office.
 *
 * Le back-office ouvre cette page dans un cadre et lui envoie les réglages en cours de
 * modification : la page se redessine sans rien enregistrer. Un clic sur un bloc lui
 * renvoie le nom du bloc, pour ouvrir le bon réglage à côté.
 *
 * Deux gardes : seuls les messages venant du back-office sont écoutés, et rien n'est
 * envoyé avant d'avoir reçu un premier message valide (on ne parle qu'à qui s'annonce).
 */

const ADMIN_ORIGINS = [
    "https://app.unisoft.world",
    "https://demo.unisoft.world",
    "https://app.kehila.io",
    "https://demo.kehila.io",
]

const LOCAL_ORIGIN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/

// Champs racine que la fiche peut faire varier dans l'aperçu.
const ROOT_FIELDS = [
    "label", "objectif", "type", "partnerMontant", "bonus", "dateStart", "dateEnd",
    "status", "donationsAfterEnd", "matched", "gala", "stockable", "tombola", "teams", "products",
]

export const MESSAGE_DRAFT = "kehila:campagne-preview"
export const MESSAGE_SELECT = "kehila:campagne-select"
export const MESSAGE_READY = "kehila:campagne-preview-ready"

/** La page est-elle servie en aperçu ? Jeton reçu dans ?preview= ET validé par l'API. */
export function isPreviewRequest(context) {
    return typeof context?.query?.preview === "string" && context.query.preview.length > 0
        && context?.org?.__campagnePreview === true
}

function isAllowedOrigin(origin) {
    if (ADMIN_ORIGINS.includes(origin)) return true
    // Serveur de développement du back-office.
    return process.env.NODE_ENV !== "production" && LOCAL_ORIGIN.test(origin || "")
}

function mergeDraft(campagneData, draft) {
    if (!draft) return campagneData
    const merged = {...campagneData}
    for (const field of ROOT_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(draft, field)) merged[field] = draft[field]
    }
    if (draft.config && typeof draft.config === "object") {
        merged.config = {...(campagneData?.config || {}), ...draft.config}
    }
    return merged
}

/**
 * @returns {{campagneData:object, preview:boolean, selectBlock:function}}
 *          `campagneData` porte les modifications en cours ; `selectBlock(nom)` prévient
 *          le back-office du bloc touché. Hors aperçu, tout est inerte.
 */
export default function usePreview(campagneData, context) {
    // Jeton vérifié par l'API (org.__campagnePreview) : un ?preview= expiré ou recopié
    // sur une page en ligne laisse la page normale, dons ouverts.
    const active = isPreviewRequest(context)
    const [draft, setDraft] = useState(null)
    const parentOrigin = useRef(null)

    useEffect(() => {
        if (!active || typeof window === "undefined" || window.parent === window.self) return

        const onMessage = (event) => {
            if (!isAllowedOrigin(event.origin)) return
            const data = event.data
            if (!data || data.type !== MESSAGE_DRAFT) return
            parentOrigin.current = event.origin
            setDraft(data.draft && typeof data.draft === "object" ? data.draft : null)
        }

        // Dans l'aperçu, un lien ne doit pas emmener le responsable ailleurs.
        const onClick = (event) => {
            const link = event.target?.closest?.("a[href]")
            if (link) event.preventDefault()
        }

        window.addEventListener("message", onMessage)
        document.addEventListener("click", onClick)
        // La fiche attend ce signal pour envoyer l'état courant.
        try {
            window.parent.postMessage({type: MESSAGE_READY}, "*")
        } catch (e) { /* cadre d'une autre origine : la fiche redemandera */ }
        return () => {
            window.removeEventListener("message", onMessage)
            document.removeEventListener("click", onClick)
        }
    }, [active])

    const selectBlock = (block, sectionId = null) => {
        if (!active || !parentOrigin.current || typeof window === "undefined") return
        try {
            window.parent.postMessage({type: MESSAGE_SELECT, block, sectionId}, parentOrigin.current)
        } catch (e) { /* le cadre a disparu */ }
    }

    const merged = useMemo(() => mergeDraft(campagneData, draft), [campagneData, draft])

    return {campagneData: merged, preview: active, selectBlock}
}
